import { unhandledRequest } from 'api/request';

import {
  JourneyListEnvelope,
  JourneyListItem,
  SaveJourneyConfigResponse,
} from '../components/types';
import { JourneyAttribute, ResolvedFieldEntry, StoredFieldConfig } from '../field-config/types';

// ---- Trigger types ----

export type TriggerType = 'stage' | 'hired' | 'rejected';

export type JobTrigger = {
  id: number;
  journeyId: string;
  journeyTitle: string;
  triggerType: TriggerType;
  jobStage: number | null;
  isAvailable: boolean;
  /**
   * Popover summary fields — real values only on the list query; the
   * create/update responses carry the server defaults ('', 0, 0) since the
   * stage-header chip and triggers sidebar always read the list query.
   */
  description: string;
  fieldsCount: number;
  preFilledCount: number;
};

export type CreateTriggerInput = {
  jobId: number;
  journeyId: string;
  triggerType: TriggerType;
  jobStage?: number | null;
};

export type UpdateTriggerInput = {
  jobId: number;
  triggerId: number;
  journeyId: string;
};

export type JobTriggerCountResponse = {
  count: number;
  /** Server-owned banner flag: default true, backfilled false for pre-ENG-500 jobs. */
  showJourneysBanner: boolean;
};

// ---- Available journeys ----

export type AvailableJourney = JourneyListItem;

export type AvailableJourneysResponse = JourneyListEnvelope<AvailableJourney>;

// ---- API functions ----
// GET, POST, and PUT all return the same enriched trigger shape: journeyTitle
// resolved against the upstream Journeys list and isAvailable computed (always
// true right after a write — writes validate the journey is available).

export async function getJobTriggers(jobId: number): Promise<JobTrigger[]> {
  const response = await unhandledRequest.get<JobTrigger[]>({
    url: `/integrations/journeys/jobs/${jobId}/triggers/`,
  });
  return response.data;
}

/**
 * GET jobs/<job_id>/triggers/count/
 *
 * Cheap trigger count for the job-header tab badge — unlike the full trigger
 * list, the server answers without calling the upstream Journeys API.
 */
export async function getJobTriggerCount(jobId: number): Promise<JobTriggerCountResponse> {
  const response = await unhandledRequest.get<JobTriggerCountResponse>({
    url: `/integrations/journeys/jobs/${jobId}/triggers/count/`,
  });
  return response.data;
}

export async function createJobTrigger(input: CreateTriggerInput): Promise<JobTrigger> {
  const response = await unhandledRequest.post<JobTrigger>({
    url: `/integrations/journeys/jobs/${input.jobId}/triggers/`,
    data: {
      journeyId: input.journeyId,
      triggerType: input.triggerType,
      jobStage: input.jobStage ?? null,
    },
  });
  return response.data;
}

export async function updateJobTrigger(input: UpdateTriggerInput): Promise<JobTrigger> {
  const response = await unhandledRequest.put<JobTrigger>({
    url: `/integrations/journeys/jobs/${input.jobId}/triggers/${input.triggerId}/`,
    data: { journeyId: input.journeyId },
  });
  return response.data;
}

export async function deleteJobTrigger(jobId: number, triggerId: number): Promise<void> {
  await unhandledRequest.delete({
    url: `/integrations/journeys/jobs/${jobId}/triggers/${triggerId}/`,
  });
}

export async function getAvailableJourneys(
  jobId: number,
  search: string,
): Promise<AvailableJourneysResponse> {
  const response = await unhandledRequest.get<AvailableJourneysResponse>({
    url: `/integrations/journeys/jobs/${jobId}/available-journeys/`,
    params: { search },
  });
  return response.data;
}

// ---- Job journey config ----

/**
 * GET jobs/<job_id>/journeys/<journey_id>/config/ response shape.
 *
 * The config endpoint opts out of global camelize/underscoreize — attributes
 * arrive in the Journeys API's camelCase (shortId, attributeType, isRequired),
 * the other keys are snake_case verbatim (resolved_fields, job_field_config,
 * resolved_base), and field_config keys are opaque mixed-case Hashids.
 *
 * job_field_config is the sparse per-job override — {} when no row exists.
 *
 * resolved_base carries the base-field prefill already merged per key — a job
 * override wins for the keys it supplies, the rest inherit from the global
 * config, and null means neither side configured any. The send sidebar reads
 * this instead of job_field_config.base so that a globally-configured mapping
 * still reaches the recruiter when the job overrides one of the other keys.
 */
export type JobJourneyConfigResponse = {
  attributes: JourneyAttribute[];
  resolved_fields: ResolvedFieldEntry[];
  /** resolve_journey_fields over the company-traveller catalog, built from the
   *  short_ids the stored config mentions — so it carries no title or
   *  attributeType, which the caller joins from its own catalog query. */
  resolved_traveller_fields?: ResolvedFieldEntry[];
  job_field_config: Partial<StoredFieldConfig>;
  /** Resolved "start journey when added": job override > global > true. */
  start_journey: boolean;
  /** The company-level value start_journey resolves from, so the per-job panel
   *  can tell an override from an inherited value. */
  global_start_journey: boolean;
  /** Base-field prefill, merged per key: job override over global. */
  resolved_base: StoredFieldConfig['base'] | null;
};

export type SaveJobJourneyConfigInput = {
  jobId: number;
  journeyId: string;
  fieldConfig: StoredFieldConfig;
};

export type SaveJobJourneyConfigResponse = SaveJourneyConfigResponse;

export async function getJobJourneyConfig(
  jobId: number,
  journeyId: string,
): Promise<JobJourneyConfigResponse> {
  const response = await unhandledRequest.get<JobJourneyConfigResponse>({
    url: `/integrations/journeys/jobs/${jobId}/journeys/${journeyId}/config/`,
  });
  return response.data;
}

export async function saveJobJourneyConfig(
  input: SaveJobJourneyConfigInput,
): Promise<SaveJobJourneyConfigResponse> {
  const response = await unhandledRequest.put<SaveJobJourneyConfigResponse>({
    url: `/integrations/journeys/jobs/${input.jobId}/journeys/${input.journeyId}/config/`,
    data: { field_config: input.fieldConfig },
  });
  return response.data;
}

// ---- Automation banner ----

export async function setJourneysBannerVisible(
  jobId: number,
  showJourneysBanner: boolean,
): Promise<void> {
  await unhandledRequest.patch({
    url: `/integrations/journeys/jobs/${jobId}/journeys-banner/`,
    data: { showJourneysBanner },
  });
}
